/**
 * Marketing copy for the packaging journey. The literals below are the defaults; the client can override
 * each one in the theme editor ("Packaging journey copy", surfaced as themeSettings.packagingCopy by
 * snippets/gift-wrap.liquid). A blank or missing setting falls back to the default here, so the journey
 * still works on a theme whose settings schema has not been pushed.
 */

/** The setting for `key` when it is a non-blank string, else `fallback`. */
export function copyText(key, fallback) {
  const value = window.giftWrapData?.themeSettings?.packagingCopy?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback;
}

/** A textarea setting, one tick per line. Falls back to the default list when no line has text. */
function copyLines(key, fallback) {
  const value = window.giftWrapData?.themeSettings?.packagingCopy?.[key];
  const lines =
    typeof value === 'string'
      ? value
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
      : [];
  return lines.length > 0 ? lines : fallback;
}

/**
 * The code defaults for the box cards. Display code should use `getBoxCopy` so theme-setting edits apply.
 */
export const BOX_COPY = {
  luxe: {
    name: 'Luxe Box',
    description: 'Our premium box, hand-tied with ribbon.',
    badge: 'Made to be kept',
    priceNote: null
  },
  signature: {
    name: 'Signature box',
    description: 'Our signature box, included with every piece.',
    badge: null,
    priceNote: 'Included'
  }
};

/**
 * Name, description, badge and price note for a box card. Luxe has no price note and Signature has no
 * badge, so those stay empty.
 * @param {'luxe' | 'signature'} role
 */
export function getBoxCopy(role) {
  const defaults = BOX_COPY[role];
  return {
    name: copyText(`${role}Name`, defaults.name),
    description: copyText(`${role}Description`, defaults.description),
    badge: defaults.badge === null ? null : copyText(`${role}Badge`, defaults.badge),
    priceNote: defaults.priceNote === null ? null : copyText(`${role}PriceNote`, defaults.priceNote)
  };
}

const PRESENTATION_DEFAULTS = {
  names: { bag: 'Personalised gift bag', keepsake: 'Personalised keepsake box', pair: 'Gift bag & gift box' },
  bagSealed: 'Optional wax seal with their initial',
  bag: ['Optional name personalisation', 'Ready to hand over'],
  keepsakeName: 'Their name on the lid',
  keepsakeRibbon: 'Ribbon-tied',
  keepsakeRibbonSealed: 'Ribbon-tied, optional wax seal with their initial',
  keepsakeInside: 'Your jewellery box sits inside, ready to hand over',
  pair: ['A gift bag and a gift box together', 'Not personalised'],
  pairSealed: 'Optional wax seal with their initial'
};

/**
 * The code defaults for the gift presentation cards (name and ticks). Display code should use
 * `getPresentationCopy` so theme-setting edits apply.
 */
export const PRESENTATION_COPY = {
  bag: {
    name: PRESENTATION_DEFAULTS.names.bag,
    ticks: ({ sealed }) => [sealed && PRESENTATION_DEFAULTS.bagSealed, ...PRESENTATION_DEFAULTS.bag].filter(Boolean)
  },
  keepsake: {
    name: PRESENTATION_DEFAULTS.names.keepsake,
    ticks: ({ sealed }) => [
      PRESENTATION_DEFAULTS.keepsakeName,
      sealed ? PRESENTATION_DEFAULTS.keepsakeRibbonSealed : PRESENTATION_DEFAULTS.keepsakeRibbon,
      PRESENTATION_DEFAULTS.keepsakeInside
    ]
  },
  pair: {
    name: PRESENTATION_DEFAULTS.names.pair,
    ticks: ({ sealed }) => [...PRESENTATION_DEFAULTS.pair, sealed && PRESENTATION_DEFAULTS.pairSealed].filter(Boolean)
  }
};

/**
 * Name and tick builder for a gift presentation card. Same shape as an entry of PRESENTATION_COPY, with
 * the theme settings applied.
 * @param {'bag' | 'keepsake' | 'pair'} kind
 */
export function getPresentationCopy(kind) {
  const d = PRESENTATION_DEFAULTS;
  const name = copyText(`${kind}Name`, d.names[kind]);
  if (kind === 'bag') {
    return {
      name,
      ticks: ({ sealed }) =>
        [sealed && copyText('bagTickSealed', d.bagSealed), ...copyLines('bagTicks', d.bag)].filter(Boolean)
    };
  }
  if (kind === 'keepsake') {
    return {
      name,
      ticks: ({ sealed }) => [
        copyText('keepsakeTickName', d.keepsakeName),
        sealed
          ? copyText('keepsakeTickRibbonSealed', d.keepsakeRibbonSealed)
          : copyText('keepsakeTickRibbon', d.keepsakeRibbon),
        copyText('keepsakeTickInside', d.keepsakeInside)
      ]
    };
  }
  return {
    name,
    ticks: ({ sealed }) =>
      [...copyLines('pairTicks', d.pair), sealed && copyText('pairTickSealed', d.pairSealed)].filter(Boolean)
  };
}

/**
 * Title bar, heading and intro for steps 1-2. The seal sentence only shows when a seal is possible; its
 * {price} placeholder is filled with the formatted wax-stamp price.
 * @param {{ step: number, sealEligible: { bag: boolean }, stampPrice: number | null, formatPrice: Function }} args
 */
export function getStepCopy({ step, sealEligible, stampPrice, formatPrice }) {
  if (step === 1) {
    return {
      titleBar: copyText('step1TitleBar', 'CHOOSE YOUR BOX'),
      heading: copyText('step1Heading', 'How would you like it boxed?'),
      intro: copyText(
        'step1Intro',
        'Every piece arrives beautifully boxed. Upgrade to our luxe box, hand-tied with ribbon.'
      )
    };
  }
  const seal =
    sealEligible.bag && stampPrice !== null
      ? ` ${copyText('step2SealSentence', 'Add a wax seal with their initial for {price}.').replaceAll('{price}', () =>
          formatPrice({ amount: stampPrice })
        )}`
      : '';
  return {
    titleBar: copyText('step2TitleBar', 'GIFT PRESENTATION'),
    heading: copyText('step2Heading', 'Make it ready to give'),
    intro: `${copyText('step2Intro', 'Make it ready to give with a gift bag.')}${seal}`
  };
}
